import { QueryKey } from '@tanstack/react-query';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { useCallback, useMemo } from 'react';
import { ApiError } from 'src/types/interfaces/api-error';
import { MeiliSearchResponse } from 'src/types/responses/meilisearch';
import { FilterOperator, FilterValue } from './types/filter-value';
import { MeiliSearchSearchState } from './types/meilisearch-search-state';
import { SearchState } from './types/search-state';
import { useMeilisearch } from './use-meilisearch';

export interface useMeilisearchReturn<T> {
  results: MeiliSearchResponse<T> | undefined;
  isLoading: boolean;
  error: ApiError | null;
  isFetching: boolean;
  handleChangeLimit: (newLimit: number) => void;
  handleChangeQuery: (value: string) => void;
  handleChangePage: (newPage: number) => void;
  handleChangeSort: (newSort: string[]) => void;
  resetFilters: () => void;
  resetSingleFilter: (filterKey: string) => void;
  handleChangeFilterValue: (
    filterKey: string,
    filterValue: FilterValue,
    filterOperator?: FilterOperator | undefined,
  ) => void;
}

interface Builders {
  [key: string]: (key: string, value: FilterValue) => string | string[];
}

const BUILDERS = {
  array: (key, value) => {
    const typedValue = value as string[] | number[] satisfies FilterValue;

    return typedValue.map(val => `${key} = ${val}`);
  },
  string: (key, value) => {
    const typedValue = value as string | number satisfies FilterValue;

    return [`${key} = ${typedValue}`];
  },
  custom: (_key, value) => {
    const typedValue = value as string satisfies FilterValue;

    // TODO: refactor to have more possible options of utils like NOW() to mark not only the place to put current date
    const replacedNowKeyword = typedValue.replace(
      /NOW\(\)/g,
      Date.now().toString(),
    );

    return replacedNowKeyword;
  },
  timerange: (key, value) => {
    const [from, to] = value as number[] satisfies FilterValue;

    // eg. "release_timestamp >= 1514761200 AND release_timestamp <= 1672527600"
    return [`${key} >= ${from} AND ${key} <= ${to}`];
  },
} satisfies Builders;

export function useMeilisearchWithUrlParams<
  TData,
  TQueryKey extends QueryKey = QueryKey,
>(
  fetch: (
    directoryToken: string,
    searchState: SearchState,
    page: number,
    perPage: number,
  ) => Promise<MeiliSearchResponse<TData>>,
  queryKey?: TQueryKey,
  defaultSearchStateConfig?: Partial<MeiliSearchSearchState>,
  enabled?: boolean,
): useMeilisearchReturn<TData> {
  const navigate = useNavigate();

  const location = useLocation();

  const buildMeilisearchFiltersFromUrlParams = (
    filters: {
      [key: string]: {
        value: FilterValue;
        operator?: FilterOperator | undefined;
      };
    } = {},
  ): SearchState['filter'] => {
    const builtFilters = [];

    const filterEntries = Object.entries(filters);

    for (const [filterKey, value] of filterEntries) {
      if (!value.value) {
        continue;
      }

      // TODO: add support for AND and OR operators, change the current operator keys to something like "builder"
      if (value.operator === 'custom') {
        if (Array.isArray(value.value)) {
          builtFilters.push(
            ...(value.value as string[]).map(val =>
              BUILDERS.custom(filterKey, val),
            ),
          );
        } else {
          builtFilters.push(BUILDERS.custom(filterKey, value.value));
        }

        continue;
      }

      if (value.operator === 'timerange') {
        builtFilters.push(BUILDERS.timerange(filterKey, value.value));

        continue;
      }

      if (Array.isArray(value.value)) {
        builtFilters.push(BUILDERS.array(filterKey, value.value));
      } else {
        builtFilters.push(BUILDERS.string(filterKey, value.value));
      }
    }

    return builtFilters;
  };

  const searchState: SearchState = useMemo(() => {
    const q = location.search?.q || (defaultSearchStateConfig?.q ?? '');

    const urlFilters = buildMeilisearchFiltersFromUrlParams(
      location.search?.filter ?? {},
    );

    const defaultFilters = defaultSearchStateConfig?.filter
      ? buildMeilisearchFiltersFromUrlParams(defaultSearchStateConfig.filter)
      : [];

    let filter: SearchState['filter'] = [];

    if (defaultSearchStateConfig?.filter && urlFilters.length === 0) {
      filter = defaultFilters;
    } else if (defaultSearchStateConfig?.filter) {
      filter = [...urlFilters, ...defaultFilters];
    } else {
      filter = urlFilters;
    }

    const sort =
      location.search?.sort && location.search.sort.length > 0
        ? location.search.sort
        : (defaultSearchStateConfig?.sort ?? []);

    return {
      q,
      filter,
      sort,
    };
  }, [
    defaultSearchStateConfig?.filter,
    defaultSearchStateConfig?.q,
    defaultSearchStateConfig?.sort,
    location.search?.filter,
    location.search?.q,
    location.search.sort,
  ]);

  const { results, error, isFetching, isLoading } = useMeilisearch<TData>({
    fetch,
    page: location?.search?.page ?? 1,
    perPage: location?.search?.limit ?? 10,
    queryKey: [...(queryKey ?? ''), location.search],
    searchState,
    enabled,
  });

  const handleChangeFilterValue = useCallback(
    (
      filterKey: string,
      filterValue: FilterValue,
      filterOperator?: FilterOperator | undefined,
    ) => {
      const filtersCopy = { ...(location.search?.filter || {}) };

      filtersCopy[filterKey] = {
        operator: filtersCopy[filterKey]?.operator ?? filterOperator,
        value: filterValue,
      };

      if (
        !filterValue ||
        (Array.isArray(filterValue) && filterValue?.length === 0)
      ) {
        delete filtersCopy[filterKey];
      }

      navigate({
        to: location.pathname,
        search: {
          sort: location.search?.sort ?? [],
          q: location.search?.q ?? '',
          filter: filtersCopy,
          page: 1,
          limit: location?.search?.limit ?? 10,
        },
        replace: true,
      });
    },
    [defaultSearchStateConfig, location.search],
  );

  const resetFilters = useCallback(() => {
    navigate({
      to: location.pathname,
      search: {
        sort: location.search?.sort ?? [],
        q: location.search?.q ?? '',
        filter: {},
        page: 1,
        limit: location?.search?.limit ?? 10,
      },
      replace: true,
    });
  }, [defaultSearchStateConfig, location.pathname, location.search]);

  const handleChangeQuery = useCallback(
    (value: string) => {
      navigate({
        to: location.pathname,
        search: {
          ...defaultSearchStateConfig,
          sort: location.search?.sort ?? [],
          q: value.toString(),
          filter: location?.search?.filter ?? {},
          page: 1,
          limit: location?.search?.limit ?? 10,
        },
        replace: true,
      });
    },
    [defaultSearchStateConfig, location.pathname, location?.search],
  );

  const handleChangePage = useCallback(
    (newPage: number) => {
      navigate({
        to: location.pathname,
        search: {
          ...defaultSearchStateConfig,
          sort: location.search?.sort ?? [],
          q: location.search?.q ?? '',
          filter: location?.search?.filter ?? {},
          page: newPage,
          limit: location?.search?.limit ?? 10,
        },
        replace: true,
      });
    },
    [defaultSearchStateConfig, location.pathname, location],
  );

  const handleChangeSort = useCallback(
    (newSort: string[]) => {
      navigate({
        to: location.pathname,
        search: {
          ...defaultSearchStateConfig,
          sort: newSort,
          q: location.search?.q ?? '',
          filter: location?.search?.filter ?? {},
          page: location?.search?.page ?? 1,
          limit: location?.search?.limit ?? 10,
        },
        replace: true,
      });
    },
    [defaultSearchStateConfig, location.pathname, location?.search],
  );

  const resetSingleFilter = useCallback(
    (filterKey: string) => {
      const filtersCopy = { ...location.search?.filter };

      delete filtersCopy[filterKey as keyof typeof filtersCopy];

      navigate({
        to: location.pathname,
        search: {
          ...defaultSearchStateConfig,
          sort: location.search?.sort ?? [],
          q: location?.search?.q ?? '',
          filter: filtersCopy,
          page: 1,
          limit: location?.search?.limit ?? 10,
        },
        replace: true,
      });
    },
    [defaultSearchStateConfig, location?.pathname, location?.search, navigate],
  );

  const handleChangeLimit = useCallback(
    (newLimit: number) => {
      navigate({
        to: location.pathname,
        search: {
          ...defaultSearchStateConfig,
          sort: location.search?.sort ?? [],
          q: location.search?.q ?? '',
          filter: location?.search?.filter ?? {},
          page: 1,
          limit: newLimit,
        },
        replace: true,
      });
    },
    [defaultSearchStateConfig, location, location.pathname],
  );

  return {
    results,
    isLoading,
    error,
    isFetching,
    handleChangeQuery,
    handleChangePage,
    handleChangeSort,
    resetFilters,
    handleChangeLimit,
    handleChangeFilterValue,
    resetSingleFilter,
  };
}
